import {
  AMENDMENT_KIND_LABELS,
  type AmendmentKind,
  amendmentOverreaches,
  type BodyChange,
  type HandoverDetail,
} from '@acasa/shared'
import { versionPdfUrl } from './use-handovers'
import { describeChange, formatDateTime } from './versions'

const SIGNATURE_NOTE: Record<AmendmentKind, string> = {
  minor: 'ohne neue Unterschriften',
  major: 'mit neuen Unterschriften',
}

/**
 * What changed, field by field.
 *
 * Stacked on a phone — label above, "bisher → neu" below — and three columns
 * from `sm` up. A three-column table at 360px leaves an address or an IBAN
 * about eight characters per line. Rows the parties signed for carry a
 * warning-coloured dot: they are what a reader of a Kleine Korrektur has to
 * look at twice.
 */
export function ChangeList({ changes }: { changes: readonly BodyChange[] }) {
  return (
    <div className="border-line border text-xs">
      <div className="border-line bg-raised text-faint hidden grid-cols-3 gap-3 border-b px-3 py-1.5 text-[10px] tracking-[0.14em] uppercase sm:grid">
        <span>Feld</span>
        <span>bisher</span>
        <span>neu</span>
      </div>
      <ul className="divide-line divide-y">
        {changes.map((change) => {
          const { before, after, status } = describeChange(change)
          return (
            <li key={change.path} className="grid gap-x-3 gap-y-0.5 px-3 py-2 sm:grid-cols-3">
              <span className="text-ink flex items-start gap-1.5">
                {change.substantive ? (
                  <span
                    className="bg-warning mt-1.5 size-1.5 shrink-0 rounded-full"
                    title="unterschriebene Angabe"
                  />
                ) : (
                  <span className="size-1.5 shrink-0" aria-hidden />
                )}
                <span className="min-w-0 break-words">
                  {change.label}
                  {change.substantive && <span className="sr-only"> (unterschriebene Angabe)</span>}
                </span>
              </span>
              <span className="text-muted min-w-0 pl-3 break-words line-through decoration-1 sm:pl-0 sm:no-underline">
                <span className="text-faint sm:hidden">bisher: </span>
                {before}
              </span>
              <span className="text-ink min-w-0 pl-3 break-words sm:pl-0">
                <span className="text-faint sm:hidden">neu: </span>
                {status === 'entfernt' ? <em className="text-muted">entfernt</em> : after}
                {status === 'hinzugefügt' && <em className="text-faint"> (hinzugefügt)</em>}
              </span>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

/**
 * The Fassungen of a protocol, newest first, each with its own PDF.
 *
 * Shown as a step of the editor rather than a separate page, because the
 * question it answers — "what did the version the tenant signed say?" — comes
 * up while looking at the protocol.
 */
export function VersionsSection({ protocol }: { protocol: HandoverDetail }) {
  const pending = protocol.status === 'amending' ? protocol.amendment : null
  const versions = [...protocol.versions].reverse()

  return (
    <div className="space-y-4">
      {pending && (
        <section className="border-warning/40 bg-warning/10 border p-4">
          <h3 className="text-ink text-sm font-medium">
            Fassung {protocol.version + 1} — in Arbeit
          </h3>
          <p className="text-muted mt-1 text-xs">
            {AMENDMENT_KIND_LABELS[pending.kind]} ({SIGNATURE_NOTE[pending.kind]})
          </p>
          <p className="text-ink mt-2 text-xs break-words">Begründung: {pending.reason}</p>
          <p className="text-faint mt-2 text-[11px]">
            begonnen {formatDateTime(pending.startedAt)}
            {pending.startedBy && ` von ${pending.startedBy.name}`}
          </p>
        </section>
      )}

      {versions.map((version) => (
        <section key={version.version} className="border-line bg-surface border p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 className="text-ink text-sm font-medium">Fassung {version.version}</h3>
              <p className="text-faint mt-0.5 text-[11px]">
                {formatDateTime(version.finalisedAt)}
                {version.finalisedBy && ` von ${version.finalisedBy.name}`}
              </p>
            </div>
            <a
              href={versionPdfUrl(protocol.id, version.version)}
              target="_blank"
              rel="noreferrer"
              className="border-line text-muted hover:text-ink shrink-0 border px-3 py-2 text-xs transition-colors"
            >
              PDF
            </a>
          </div>

          {version.amendment === null ? (
            <p className="text-muted mt-2 text-xs">ursprüngliche Fassung</p>
          ) : (
            <>
              <p className="text-muted mt-2 text-xs">
                {AMENDMENT_KIND_LABELS[version.amendment.kind]} (
                {SIGNATURE_NOTE[version.amendment.kind]})
              </p>
              <p className="text-ink mt-1 text-xs break-words">
                Begründung: {version.amendment.reason}
              </p>
            </>
          )}

          {amendmentOverreaches(version) && (
            <p className="text-muted mt-2 text-[11px]">
              Korrektur betrifft unterschriebene Angaben.
            </p>
          )}

          {version.amendment !== null && (
            <div className="mt-3">
              {version.changes.length > 0 ? (
                <ChangeList changes={version.changes} />
              ) : (
                <p className="text-faint text-[11px]">Keine Feldänderungen erfasst.</p>
              )}
            </div>
          )}
        </section>
      ))}
    </div>
  )
}
